import { fetchConfigureTexts } from '@intelligence/services/common';

import type { Action, Reducer, Effect } from 'umi';

export interface IConfigureTextsState {
  loading: boolean;
  data: IConfigureTexts;
}

type IUpdateConfigureTextsLoadingAction = Action<'updateConfigureTextsLoading'> & {
  type: string;
  payload: boolean;
};

type IUpdateConfigureTextsAction = Action<'updateConfigureTexts'> & {
  type: string;
  payload: IConfigureTexts;
};

export interface IConfigureTextsModel {
  namespace: string;
  state: IConfigureTextsState;
  effects: {
    getConfigureTexts: Effect;
  };
  reducers: {
    updateConfigureTextsLoading: Reducer<IConfigureTextsState, IUpdateConfigureTextsLoadingAction>;
    updateConfigureTexts: Reducer<IConfigureTextsState, IUpdateConfigureTextsAction>;
  };
}

export const CONFIGURE_TEXTS_MODEL_NAMESPACE = 'dbi-model-configure-texts';

export const DEFAULT_CONFIGURE_TEXTS: IConfigureTexts = {
  en: {},
  cn: {},
};

export const DEFAULT_CONFIGURE_TEXTS_STATE: IConfigureTextsState = {
  loading: true,
  data: DEFAULT_CONFIGURE_TEXTS,
};

/**
 * 将包含语言后缀的键值对对象转换为按语言分类的配置文本对象
 *
 * @param data - 原始数据对象，键名应包含 '_en' 或 '_cn' 后缀表示语言
 *               (例如: { "title_en": "Hello", "title_cn": "你好" })
 * @returns 返回按语言分类的配置对象，结构为:
 *          {
 *            en: { [key: string]: string },  // 英文配置项(已移除'_en'后缀)
 *            cn: { [key: string]: string }   // 中文配置项(已移除'_cn'后缀)
 *          }
 *
 * @example
 * // 输入:
 * const input = {
 *   "welcome_en": "Hello",
 *   "welcome_cn": "你好",
 *   "button_submit_en": "Submit"
 * };
 *
 * // 输出:
 * {
 *   en: { welcome: "Hello", button_submit: "Submit" },
 *   cn: { welcome: "你好" }
 * }
 *
 * @remarks
 * 1. 如果输入为 null/undefined，返回 { en: {}, cn: {} }
 * 2. 不符合 _en/_cn 后缀的键会被忽略
 * 3. 键名中的语言后缀会被自动移除
 */
const convert2ConfigureTexts = (data?: Record<string, string> | null): IConfigureTexts => {
  if (!data) return { en: {}, cn: {} };

  const configureTexts: IConfigureTexts = {
    en: {},
    cn: {},
  };

  // 遍历data对象的所有键
  Object.keys(data).forEach((key) => {
    const value = data[key];

    // 处理英文文本（以_en结尾的键）
    if (key?.endsWith('_en')) {
      const newKey = key.replace(/_en$/, ''); // 移除_en后缀
      configureTexts.en[newKey] = value;
    }
    // 处理中文文本（以_cn结尾的键）
    else if (key?.endsWith('_cn')) {
      const newKey = key.replace(/_cn$/, ''); // 移除_cn后缀
      configureTexts.cn[newKey] = value;
    }
    // 可以添加其他情况的处理，或者忽略不符合规则的键
  });

  return configureTexts;
};

const ConfigureTextsModel: IConfigureTextsModel = {
  namespace: CONFIGURE_TEXTS_MODEL_NAMESPACE,
  state: DEFAULT_CONFIGURE_TEXTS_STATE,
  effects: {
    *getConfigureTexts(action, { call, put }) {
      yield put({ type: 'updateConfigureTextsLoading', payload: true });

      const { data } = yield call(fetchConfigureTexts, action?.payload);

      yield put({ type: 'updateConfigureTextsLoading', payload: false });

      yield put({ type: 'updateConfigureTexts', payload: convert2ConfigureTexts(data?.configure_texts) });
    },
  },
  reducers: {
    updateConfigureTextsLoading(state, action) {
      return { ...state, loading: !!action?.payload };
    },
    updateConfigureTexts(state, action) {
      return { ...state, data: action?.payload || DEFAULT_CONFIGURE_TEXTS };
    },
  },
};

export default ConfigureTextsModel;
